// Active jobs view for the portal — status not in (complete, cancelled).
// Queries portal_jobs (the view), NEVER raw field_jobs. The view's
// security_invoker=on means RLS applies; the column projection is the
// view's contract. See migration 20260607003457.
//
// Photo thumbnails: signed URLs via supabase.storage.from(...). The new
// "Portal users can read job photos" storage policy (migration
// 20260607004801) gates portal storage access via the photo→job→company
// chain — so getJobPhotoSignedUrl works for portal sessions without code
// changes to the helper.

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { formatDate } from '@/lib/utils'
import { Calendar, MapPin, User as UserIcon, Phone, Briefcase, ChevronRight } from 'lucide-react'
import { JOB_STATUS_CONFIG } from '@/pages/field/FieldHub'
import { PortalPhotoStrip } from '@/pages/portal/PortalPhotoStrip'
import { portalListVariants, portalCardVariants, portalCardHover } from '@/pages/portal/portalMotion'
import { PortalSkeletonList } from '@/pages/portal/PortalSkeleton'

interface PortalJob {
  id: string
  job_number: string | null
  title: string | null
  status: string
  scheduled_date: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  address_line1: string | null
  address_line2: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  job_notes: string | null
  assigned_agent_name: string | null
  csr_name: string | null
}

function useActivePortalJobs() {
  return useQuery({
    queryKey: ['portal-jobs-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_jobs')
        .select('id, job_number, title, status, scheduled_date, scheduled_start, scheduled_end, address_line1, address_line2, address_city, address_state, address_zip, job_notes, assigned_agent_name, csr_name')
        .not('status', 'in', '(complete,cancelled)')
        .order('scheduled_date', { ascending: true, nullsFirst: false })
      if (error) throw error
      return (data ?? []) as PortalJob[]
    },
  })
}

export default function PortalJobs() {
  const { data: jobs = [], isLoading } = useActivePortalJobs()

  if (isLoading) {
    return <PortalSkeletonList count={3} height={120} />
  }
  if (jobs.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <Briefcase style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>No active jobs right now</p>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>When a visit is scheduled, you'll see it here. Completed work lives under History.</p>
      </div>
    )
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={portalListVariants}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {jobs.map(job => <PortalJobCard key={job.id} job={job} />)}
    </motion.div>
  )
}

function PortalJobCard({ job }: { job: PortalJob }) {
  const navigate = useNavigate()
  const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
  const address = useMemo(() => composeAddress(job), [job])
  const schedule = useMemo(() => composeSchedule(job), [job])

  // Whole-card tap target navigates to the detail page; nested anchors
  // (photo thumbnails) stop short-circuit via closest('a') so a thumb
  // click still opens the lightbox in a new tab.
  const onCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) return
    navigate(`/portal/jobs/${job.id}`)
  }

  return (
    <motion.article variants={portalCardVariants} whileHover={portalCardHover}
      role="link" tabIndex={0}
      onClick={onCardClick}
      onKeyDown={e => { if (e.key === 'Enter') navigate(`/portal/jobs/${job.id}`) }}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${cfg.color}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{job.job_number ?? ''}</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
          {cfg.label}
        </span>
        <ChevronRight style={{ width: 12, height: 12, color: 'var(--ink-tertiary)', marginLeft: 'auto' }} />
      </div>
      <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{job.title ?? 'Untitled'}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
        {schedule && <Meta icon={<Calendar style={iconStyle} />} text={schedule} />}
        {address && <Meta icon={<MapPin style={iconStyle} />} text={address} />}
        {job.assigned_agent_name && <Meta icon={<UserIcon style={iconStyle} />} text={`Tech: ${job.assigned_agent_name}`} />}
        {job.csr_name && <Meta icon={<Phone style={iconStyle} />} text={`Coordinator: ${job.csr_name}`} />}
      </div>
      {job.job_notes && job.job_notes.trim() && (
        <div style={{ marginBottom: 10 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Details</p>
          <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{job.job_notes}</p>
        </div>
      )}
      <PortalPhotoStrip jobId={job.id} />
    </motion.article>
  )
}

function Meta({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-secondary)' }}>
      {icon}
      <span>{text}</span>
    </div>
  )
}

const iconStyle: React.CSSProperties = { width: 12, height: 12, color: 'var(--ink-tertiary)', flexShrink: 0 }

// ─── helpers ────────────────────────────────────────────────────────
function composeAddress(j: PortalJob): string | null {
  const street = [j.address_line1, j.address_line2].filter(Boolean).join(', ')
  const region = [j.address_city, j.address_state].filter(Boolean).join(', ')
  const full = [street, region, j.address_zip].filter(Boolean).join(' · ')
  return full || null
}
function composeSchedule(j: PortalJob): string | null {
  if (!j.scheduled_date && !j.scheduled_start) return null
  const parts: string[] = []
  if (j.scheduled_date) parts.push(formatDate(j.scheduled_date, 'EEE, MMM d'))
  if (j.scheduled_start && j.scheduled_end) parts.push(`${j.scheduled_start}–${j.scheduled_end}`)
  else if (j.scheduled_start) parts.push(j.scheduled_start)
  return parts.join(' · ')
}
